/**
 * Current raid bosses, grouped by tier — Profile page.
 *
 * "What can I raid right now, and which of those do I still need?" The roster
 * comes from GET /api/events/raids (LeekDuck via ScrapedDuck, the same upstream
 * the event feed already uses, so it stays current automatically). Each tier —
 * 1★ / 3★ / 5★ / Mega — renders as its own row.
 *
 * A boss the trainer is MISSING from their dex is a silhouette with a "NEED"
 * flag and deep-links into the matching Pokédex tab; one they already have is
 * shown in colour and dimmed slightly, so the row reads at a glance as "these
 * three are worth my raid passes". The variant checked per boss follows what the
 * raid actually yields: a Mega raid is checked against the Mega dex, a Shadow
 * raid against Shadow, everything else against the base dex — plus Shiny when
 * the boss can be shiny and the trainer already owns the base copy.
 *
 * Attribution to LeekDuck is required wherever this data renders (ScrapedDuck's
 * terms), so the footer credit is not optional chrome.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Swords, ArrowRight, Lock } from "lucide-react";
import { InfoBlock } from "@/components/InfoBlock";
import { EvoLinesToggle } from "@/components/EvoLinesToggle";
import { useEvoLines } from "@/lib/evoLinesPref";
import { evolutionStagesFor } from "@/lib/eventEvoLines";
import { formSuffix } from "@/lib/costumeEvolution";
import { speciesName } from "@/data/speciesNames";
import { CaughtSprite } from "@/components/CaughtSprite";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { getVariantLabel } from "@/components/dexVariantVisuals";
import { hasProAccess, useSession } from "@/components/SessionProvider";
import { DEX_STATE_EVENT, getDexEntryState } from "@/lib/dexStateStore";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { raidBossArtCandidates, raidBossSilhouetteSlug } from "@/lib/formSpriteCandidates";
import { applyRaidDaySchedule } from "@/lib/raidDaySchedule";
import {
  RAID_TIER_ORDER,
  raidTierLabel,
  raidBossFormId,
  raidBossVariant,
  type RaidBossDto,
  type RaidTierKey,
} from "@/lib/raidRoster";

interface RaidsResponse {
  tiers?: Partial<Record<string, RaidBossDto[]>>;
  attribution?: { source: string; sourceUrl: string; feed: string; feedUrl: string };
}

/**
 * "Hunting:" dex selector (2026-08-25 ask, Pro). The trainer picks a dex
 * and every live boss is checked against THAT dex. Second pass (same day):
 * the list is the five hunt-worthy dexes only — Shiny (the default; there
 * is no "raid rewards" mode any more), Lucky, 100%, XXS, XXL. Main, Mega,
 * Shadow, Purified and the custom Shundo / Shiny Lucky dexes came out, and
 * Dynamax / G-Max were never in (a raid catch can't fill either).
 */
const HUNTING_VARIANTS: readonly PokedexVariant[] = [
  "shiny",
  "lucky",
  "perfect",
  "xxs",
  "xxl",
];

const DEFAULT_HUNTING: PokedexVariant = "shiny";
const HUNTING_STORAGE_KEY = "nexus.raidHunting.v1";

const REGION_WORD: Readonly<Record<string, string>> = { alola: "Alolan", galar: "Galarian", hisui: "Hisuian", paldea: "Paldean" };

/** Upstream-style label for a stage the Evo switch added ("Shadow Haunter", "Alolan Sandslash"). */
function stageLabel(speciesId: number, suffix: string | null, shadow: boolean): string {
  const word = suffix ? REGION_WORD[suffix] : undefined;
  const name = word ? `${word} ${speciesName(speciesId)}` : speciesName(speciesId);
  return shadow ? `Shadow ${name}` : name;
}

/**
 * Evo (2026-10-20 owner ask, "Raids also needs the evo toggle"): after each
 * boss, the rest of its line as rows of their own, so a raid Sneasel shows
 * the Weavile still missing in the hunted dex and a Mega Charizard the
 * Charmander and Charmeleon (whole line, like the Dex's toggle: owner
 * 2026-10-31). A stage the tier already lists is not added twice. `via`
 * names the boss a row came from; the row itself is the plain stage.
 */
function lineUp(bosses: readonly RaidBossDto[], evoLines: boolean): Array<RaidBossDto & { via?: string }> {
  if (!evoLines) return [...bosses];
  const key = (speciesId: number | undefined, formId: string | null) => `${speciesId ?? ""}:${formId ?? ""}`;
  const seen = new Set(bosses.map((b) => key(b.speciesId, raidBossFormId(b))));
  const out: Array<RaidBossDto & { via?: string }> = [];
  for (const boss of bosses) {
    out.push(boss);
    if (typeof boss.speciesId !== "number" || boss.speciesId < 1) continue;
    for (const st of evolutionStagesFor(boss.speciesId, raidBossFormId(boss))) {
      const k = key(st.speciesId, st.formId);
      if (seen.has(k)) continue;
      seen.add(k);
      const suffix = st.formId ? formSuffix(st.formId) : null;
      const row: RaidBossDto & { via?: string } = {
        ...boss,
        speciesId: st.speciesId,
        name: stageLabel(st.speciesId, suffix, boss.isShadow),
        isMega: false,
        via: boss.name,
      };
      delete row.image;
      delete row.hundoCp;
      if (suffix) row.formSuffix = suffix;
      else delete row.formSuffix;
      out.push(row);
    }
  }
  return out;
}

function readStoredHunting(): PokedexVariant {
  try {
    const raw = window.localStorage.getItem(HUNTING_STORAGE_KEY);
    // A stored value from the wider v1 list (mega, shundo…) falls back to
    // the Shiny default rather than resurrecting a removed option.
    return raw && (HUNTING_VARIANTS as readonly string[]).includes(raw)
      ? (raw as PokedexVariant)
      : DEFAULT_HUNTING;
  } catch {
    return DEFAULT_HUNTING;
  }
}

function storeHunting(v: PokedexVariant): void {
  try {
    window.localStorage.setItem(HUNTING_STORAGE_KEY, v);
  } catch {
    /* private mode — the pick just doesn't persist */
  }
}

export function RaidBossPanel() {
  // STALENESS (2026-08-31 device report: a rotation that flipped at 10am
  // local was still showing the old bosses). The upstream snapshot has no
  // time windows, so freshness is all we control: 5min staleTime + a 15min
  // interval while the panel is mounted (the global defaults are
  // staleTime: Infinity / no refetching, so a long-lived Capacitor webview
  // NEVER refetched inside a session), plus the app-resume refetch below.
  const raidsQ = useQuery<RaidsResponse>({
    queryKey: ["/api/events/raids"],
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  });
  // Returning from background is the moment a rotation is most likely to
  // have flipped underneath us. appStateChange, not visibilitychange: the
  // latter is unreliable in WKWebView.
  const refetchRaids = raidsQ.refetch;
  useEffect(() => {
    let remove: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app" as any);
        const handle = await App.addListener("appStateChange", (s: { isActive: boolean }) => {
          if (s?.isActive) void refetchRaids();
        });
        remove = () => { void handle.remove(); };
      } catch {
        /* not a Capacitor runtime; the interval above still covers it. */
      }
    })();
    return () => { remove?.(); };
  }, [refetchRaids]);
  const { tier, openPaywall } = useSession();
  const isPro = hasProAccess(tier);
  const [, setLocation] = useLocation();
  const [evoLines] = useEvoLines();
  // The hunted dex, Pro-only, Shiny by default. Free accounts always run
  // the raid-yield default, whatever an old localStorage value says.
  const [hunting, setHunting] = useState<PokedexVariant>(() =>
    typeof window === "undefined" ? DEFAULT_HUNTING : readStoredHunting(),
  );
  const activeHunting = isPro ? hunting : null;

  // Re-read on dex-state mutations so a boss drops out of "need" the moment the
  // trainer records the catch. Mirrors the Calendar / Profile gap strips.
  const [dexTick, setDexTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const bump = () => setDexTick((n) => n + 1);
    window.addEventListener(DEX_STATE_EVENT, bump);
    return () => window.removeEventListener(DEX_STATE_EVENT, bump);
  }, []);

  const tiers = useMemo(() => {
    // The day schedule (raidDaySchedule.ts): an event that rotates its Mega
    // bosses daily outruns the roster feed, so today's bosses come from it.
    const data = applyRaidDaySchedule(raidsQ.data?.tiers ?? {});
    return RAID_TIER_ORDER.map((tier) => ({ tier, bosses: data[tier] ?? [] })).filter(
      (t) => t.bosses.length > 0,
    );
  }, [raidsQ.data]);

  // Nothing to show and nothing in flight: the roster is genuinely empty or the
  // upstream is down. Rendering an empty titled block would be noise on the
  // spine, so the whole panel stands down.
  if (!raidsQ.isLoading && tiers.length === 0) return null;

  return (
    <InfoBlock
      icon={Swords}
      title="Raids right now"
      trailing={<EvoLinesToggle testId="profile-raids-evo" />}
      data-testid="profile-raid-bosses"
    >
      {/* "Hunting:" — pick any dex and every live boss is checked against IT
          (2026-08-25 ask). Pro feature; free accounts get the tease. */}
      <div className="mb-3" data-testid="raid-hunting-row">
        {isPro ? (
          <div className="flex items-center gap-2 flex-wrap">
            <label
              htmlFor="raid-hunting-select"
              className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-tertiary)] shrink-0"
            >
              Hunting:
            </label>
            <select
              id="raid-hunting-select"
              value={hunting}
              onChange={(e) => {
                const next = e.target.value as PokedexVariant;
                setHunting(next);
                storeHunting(next);
              }}
              data-testid="raid-hunting-select"
              className="flex-1 min-w-0 rounded-lg px-2 py-1.5 text-[12px] font-bold"
              style={{
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text-ink-bright)",
                minHeight: 36,
              }}
            >
              {/* Bare dex names (2026-08-25 second pass): "Shiny", not
                  "Shiny dex" — the label already says what's being picked. */}
              {HUNTING_VARIANTS.map((v) => (
                <option key={v} value={v}>
                  {getVariantLabel(v)}
                </option>
              ))}
            </select>
            {activeHunting && (
              <button
                type="button"
                onClick={() => setLocation(`/pokedex?filter=${activeHunting}`)}
                data-testid="raid-hunting-dex-link"
                className="inline-flex items-center gap-1 text-[11px] font-bold shrink-0"
                style={{ color: "var(--brand-green-on-light)", minHeight: 32 }}
              >
                Open {getVariantLabel(activeHunting)} dex <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          // The TEASE: name exactly what Pro unlocks here, one tap from the
          // paywall — same gate-at-the-value pattern as the other Pro teases.
          <button
            type="button"
            onClick={() =>
              openPaywall(
                "raid-hunting",
                "Pick a dex — Shiny, Lucky, 100%, XXS or XXL — and see which live raid bosses it's still missing.",
              )
            }
            data-testid="raid-hunting-tease"
            className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-left"
            style={{
              background: "var(--surface-sunken)",
              border: "1px dashed var(--accent-fill-glow)",
              minHeight: 40,
            }}
          >
            <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--accent-fill)" }} />
            <span className="min-w-0">
              <span className="block text-[11px] font-bold text-[var(--text-ink-bright)]">
                Hunting: any dex <span style={{ color: "var(--accent-fill)" }}>· Pro</span>
              </span>
              <span className="block text-[10px] text-[var(--text-secondary)] truncate">
                Check these bosses against your Shiny, 100% or XXL dex.
              </span>
            </span>
          </button>
        )}
      </div>

      {raidsQ.isLoading ? (
        <p className="text-[11px] text-[var(--text-secondary)]" data-testid="profile-raids-loading">
          Loading the raid roster…
        </p>
      ) : (
        <div className="space-y-4">
          {tiers.map(({ tier, bosses }) => (
            <TierRow
              key={tier}
              tier={tier}
              bosses={bosses}
              dexTick={dexTick}
              hunting={activeHunting}
              evoLines={evoLines}
            />
          ))}
        </div>
      )}
    </InfoBlock>
  );
}

function TierRow({
  tier,
  bosses,
  dexTick,
  hunting,
  evoLines = false,
}: {
  tier: RaidTierKey;
  bosses: readonly RaidBossDto[];
  dexTick: number;
  /** Pro "Hunting:" dex — every boss is checked against THIS dex instead of
   * the raid-yield default. Null = default behaviour. */
  hunting: PokedexVariant | null;
  /** The Evo switch: each boss followed by the rest of its line. */
  evoLines?: boolean;
}) {
  const [, setLocation] = useLocation();

  const rows = useMemo(
    () => {
      void dexTick; // recompute when dex state changes
      return lineUp(bosses, evoLines).map((boss) => {
        const yieldVariant = raidBossVariant(boss);
        // Unresolvable species (upstream named a boss we can't map to a dex id)
        // still render — the trainer can read the name — but we never claim to
        // know whether they have it.
        const known = typeof boss.speciesId === "number" && boss.speciesId >= 1;
        // A regional boss is checked against THAT form, not the base species:
        // owning a Kanto Darumaka says nothing about the Galarian one.
        const formId = raidBossFormId(boss);
        if (hunting && known) {
          // Hunting mode: the SELECTED dex is the only question. A species
          // that can't exist for that dex (no Mega, shiny not released, not
          // tradable for Lucky…) is honestly out of scope — no flag, dimmed.
          const inScope = isVariantAvailable(hunting, boss.speciesId!);
          const present =
            inScope &&
            getDexEntryState(hunting, boss.speciesId!, formId)?.state === "present";
          const artRef = {
            speciesId: boss.speciesId!,
            isMega: yieldVariant === "mega",
            name: boss.name,
            formId,
          };
          return {
            boss,
            variant: hunting,
            known,
            present,
            shinyGap: false,
            inScope,
            formId,
            artRef,
          };
        }
        const present =
          known && getDexEntryState(yieldVariant, boss.speciesId!, formId)?.state === "present";
        // A shiny is only worth flagging when the boss can actually be shiny AND
        // the trainer already has the base copy — otherwise the honest gap is the
        // base one, which `present` already covers.
        const shinyGap =
          known &&
          present &&
          boss.canBeShiny &&
          isVariantAvailable("shiny", boss.speciesId!) &&
          getDexEntryState("shiny", boss.speciesId!, formId)?.state !== "present";
        // Shared art shape (raidBossArtCandidates/raidBossSilhouetteSlug): a
        // Mega boss resolves true Mega art, X-vs-Y picked from upstream's name.
        const artRef = known
          ? { speciesId: boss.speciesId!, isMega: yieldVariant === "mega", name: boss.name, formId }
          : null;
        return { boss, variant: yieldVariant, known, present, shinyGap, inScope: true, formId, artRef };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bosses, dexTick, hunting, evoLines],
  );

  const needed = rows.filter(
    (r) => r.known && r.inScope && (!r.present || r.shinyGap),
  ).length;

  return (
    <div data-testid={`profile-raid-tier-${tier}`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)]">
          {raidTierLabel(tier)}
        </p>
        {needed > 0 && (
          <span
            className="text-[9px] font-bold uppercase tracking-[0.14em]"
            style={{ color: "var(--brand-green-on-light)" }}
            data-testid={`profile-raid-tier-need-${tier}`}
          >
            {needed} to chase
          </span>
        )}
      </div>
      {/* Centred, like the gap strips: a tier is usually two or three bosses,
          and left-aligned they hung off the card's left edge under a heading
          row that spans it (2026-08-31 device report). */}
      <ul className="flex flex-wrap justify-center gap-2">
        {rows.map(({ boss, variant, known, present, shinyGap, inScope, formId, artRef }, i) => {
          // Upstream's name is the precise one ("Galarian Darumaka", "Shadow
          // Snorlax"); the species-name lookup would flatten it to the base
          // species, so it is the label everywhere — caption and aria alike.
          const label = boss.name;
          const flag =
            !known || !inScope ? null : !present ? "Need" : shinyGap ? "Shiny" : null;
          return (
            <li key={`${boss.name}-${i}`}>
              <button
                type="button"
                disabled={!known}
                onClick={() =>
                  known &&
                  setLocation(
                    `/pokedex?filter=${shinyGap ? "shiny" : variant}&dex=${boss.speciesId}`,
                  )
                }
                // Form-qualified: a tier can list a species twice (Darumaka AND
                // Galarian Darumaka), and a species-only id makes the two rows
                // indistinguishable to anything reading the DOM.
                data-testid={`profile-raid-boss-${tier}-${boss.speciesId ?? boss.name}${formId ? `-${formId}` : ""}`}
                data-need={flag ? "" : undefined}
                data-via={boss.via}
                aria-label={
                  !known
                    ? label
                    : !inScope
                      ? `${label}, not available in this dex`
                      : flag
                        ? `${label}, ${flag === "Need" ? "still missing" : "shiny still missing"}`
                        : `${label}, already caught`
                }
                className="relative w-16 rounded-xl p-1 bg-[var(--surface-sunken)] border border-[var(--border-subtle)] hover-elevate flex flex-col items-center disabled:cursor-default"
                style={{
                  // Dimmed = "not a chase": already caught, or (hunting mode)
                  // the species can't exist for the hunted dex at all.
                  opacity: known && ((present && !shinyGap) || !inScope) ? 0.55 : 1,
                }}
              >
                {/* A gap is a silhouette, never a caught-looking sprite — the
                    whole point of the row is telling the two apart at a glance. */}
                {known && present && artRef ? (
                  <CaughtSprite
                    id={artRef.speciesId}
                    size={38}
                    shiny={false}
                    ariaLabel={label}
                    // The artwork comes from formSpriteCandidates; formSlug only
                    // picks a silhouette tile. Passing the slug alone rendered a
                    // caught Galarian Darumaka as the red Kanto one.
                    formSpriteCandidates={raidBossArtCandidates(artRef)}
                    formSlug={raidBossSilhouetteSlug(artRef)}
                  />
                ) : (
                  <PokemonSilhouette
                    id={known ? boss.speciesId! : 0}
                    size={38}
                    caught={false}
                    shiny={false}
                  />
                )}
                {flag && (
                  <span
                    className="absolute -top-1 -right-1 text-[7px] font-extrabold uppercase tracking-wide px-1 py-0.5 rounded-full"
                    style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)" }}
                  >
                    {flag}
                  </span>
                )}
                <span className="mt-0.5 w-full truncate text-center text-[7.5px] leading-tight text-[var(--text-tertiary)]">
                  {boss.name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
